import * as React from 'react';
import { createRootRouteWithContext, Outlet, redirect, useLocation } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { UserProfile, EndpointDef } from '@ooh/contracts';
import { callEndpoint } from '@ooh/contracts';
import { AppShell } from '../components/app-shell';
import { AppUpdateBanner } from '../components/app-update-banner';
import { Toaster } from '@ooh/ui/components/sonner';
import { DefaultPending } from '../components/default-pending';
import { ScreensProvider } from '../components/screens-provider';
import { isPublicRoutePath } from '../lib/public-route';

// ─── Page title context ───────────────────────────────────────
// Allows child routes to set the page title rendered in AppShell's top bar.

export const PageTitleContext = React.createContext<{
  setPageTitle: (title: string) => void;
}>({ setPageTitle: () => {} });

export interface RouterContext {
  queryClient: QueryClient;
  auth: {
    accessToken: string | null;
    user: UserProfile | null;
    permissions: string[] | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    refresh: () => Promise<boolean>;
    ensurePermissions: () => Promise<void>;
    callWithAuth: <TDef extends EndpointDef>(
      def: TDef,
      request: Parameters<typeof callEndpoint<TDef>>[1],
      options?: { signal?: AbortSignal },
    ) => ReturnType<typeof callEndpoint<TDef>>;
  };
}

export interface AuthGuardParams {
  accessToken: string | null;
  pathname: string;
  refresh: () => Promise<boolean>;
  throwRedirect: (path: string) => never;
}

/**
 * Pure guard logic — extracted for unit testing.
 * When no access token is present, attempts a silent refresh.
 * If refresh fails and the target is not /login, redirects to /login.
 */
export async function handleRootAuthGuard({
  accessToken,
  pathname,
  refresh,
  throwRedirect,
}: AuthGuardParams): Promise<void> {
  if (accessToken !== null) {
    // Already authenticated — allow through
    return;
  }

  // No token in memory — attempt silent refresh (e.g. page reload with valid cookie)
  const refreshed = await refresh();

  if (refreshed) {
    // Session restored — allow through
    return;
  }

  // Refresh failed — only redirect if not on /login or /public/* routes
  if (pathname !== '/login' && !isPublicRoutePath(pathname)) {
    throwRedirect('/login');
  }
}

function RootComponent() {
  const location = useLocation();
  const isShellless = location.pathname === '/login' || isPublicRoutePath(location.pathname);
  const [pageTitle, setPageTitle] = React.useState('');
  const pageTitleContextValue = React.useMemo(() => ({ setPageTitle }), []);

  if (isShellless) {
    return (
      <ScreensProvider>
        <Toaster />
        <AppUpdateBanner position="top" />
        <Outlet />
      </ScreensProvider>
    );
  }

  return (
    <ScreensProvider>
      <Toaster />
      <AppUpdateBanner />
      <PageTitleContext.Provider value={pageTitleContextValue}>
        <AppShell pageTitle={pageTitle}>
          <Outlet />
        </AppShell>
      </PageTitleContext.Provider>
    </ScreensProvider>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    await handleRootAuthGuard({
      accessToken: context.auth.accessToken,
      pathname: location.pathname,
      refresh: context.auth.refresh,
      throwRedirect: (path) => {
        throw redirect({ to: path });
      },
    });
    // Ensure permissions are loaded even when a stored token bypasses the
    // normal login/refresh flow (e.g. Playwright storageState injection).
    await context.auth.ensurePermissions();
  },
  pendingComponent: DefaultPending,
  component: RootComponent,
});
